'use client';

import { useRef, useEffect } from 'react';

/**
 * Writes `value` into an EXISTING ref after each render that changes it -- inside an effect, NOT the render body, so a
 * concurrent render cannot expose a torn reference. The half of {@link useLatestRef} for a ref that must exist BEFORE
 * its value does: a flush that reschedules itself reads the ref its own scheduler is later latched into.
 */
export function useLatestInto<T>(ref: { current: T }, value: T): void {
	useEffect(() => {
		ref.current = value;
	}, [ref, value]);
}

/** Returns a ref that always holds the latest value -- for reading current props/state in callbacks without re-renders or stale closures. */
export function useLatestRef<T>(value: T) {
	const ref = useRef(value);
	useLatestInto(ref, value);
	return ref;
}
